import { ChevronDown, Check } from 'lucide-react'
import { ParentTemplate } from '../../types/template'
import { Card } from '../ui/card'
import { cn } from '../../lib/utils'
import { Image as ImageIcon } from 'lucide-react'

interface ParentTemplateCardProps {
  template: ParentTemplate
  isExpanded: boolean
  onToggleExpand: () => void
  /**
   * Handler for selecting the template (theme mode)
   */
  onSelect?: () => void
  /**
   * Whether this parent template is selected (for theme mode)
   */
  isSelected?: boolean
  /**
   * Selection mode: 'layout' shows expand chevron, 'theme' shows select behavior
   */
  selectionMode?: 'layout' | 'theme'
}

export function ParentTemplateCard({
  template,
  isExpanded,
  onToggleExpand,
  onSelect,
  isSelected = false,
  selectionMode = 'layout',
}: ParentTemplateCardProps) {
  // Use first child layout for preview
  const firstLayout = template.layouts[0]

  // Handle card click based on mode
  const handleCardClick = () => {
    if (selectionMode === 'theme' && onSelect) {
      // In theme mode, clicking the main area selects the template
      onSelect()
    } else {
      // In layout mode, clicking expands/collapses
      onToggleExpand()
    }
  }

  // Handle expand button click (for theme mode)
  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleExpand()
  }

  return (
    <Card
      className={cn(
        'relative overflow-hidden cursor-pointer transition-all duration-200 group',
        'border-2',
        // Distinct styling for parent cards
        // In theme mode, show selection state
        selectionMode === 'theme' && isSelected
          ? 'ring-2 ring-green-500 border-green-500 bg-green-50/50 dark:bg-green-950/20 shadow-lg'
          : isExpanded
            ? 'ring-2 ring-primary border-primary bg-accent/50 shadow-lg'
            : 'border-primary/30 bg-accent/30 hover:border-primary hover:shadow-lg hover:bg-accent/50 dark:hover:bg-accent/20'
      )}
      onClick={handleCardClick}
    >
      <div className="flex flex-col h-[280px]">
        {/* Image Preview */}
        <div className="flex-1 relative overflow-hidden bg-slate-50 dark:bg-slate-900">
          <div className="w-full h-full flex items-center justify-center p-4">
            {firstLayout?.previewUrl ? (
              <img
                src={firstLayout.previewUrl}
                alt={template.name}
                className="max-w-full max-h-full object-contain rounded"
                loading="lazy"
              />
            ) : (
              <div className="text-center text-muted-foreground">
                <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No preview available</p>
              </div>
            )}
          </div>

          {/* Color palette indicator - always visible in top-right */}
          <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
            {/* Color palette dots */}
            <div className="flex gap-1 bg-white/95 dark:bg-black/95 rounded-full px-1.5 py-1 shadow-sm border border-gray-200/50">
              {template.colorPalette?.primary && (
                <div
                  className="w-2.5 h-2.5 rounded-full border border-gray-300"
                  style={{ backgroundColor: template.colorPalette.primary }}
                  title={`Primary: ${template.colorPalette.primary}`}
                />
              )}
              {template.colorPalette?.secondary && (
                <div
                  className="w-2.5 h-2.5 rounded-full border border-gray-300"
                  style={{ backgroundColor: template.colorPalette.secondary }}
                  title={`Secondary: ${template.colorPalette.secondary}`}
                />
              )}
              {template.colorPalette?.accent && (
                <div
                  className="w-2.5 h-2.5 rounded-full border border-gray-300"
                  style={{ backgroundColor: template.colorPalette.accent }}
                  title={`Accent: ${template.colorPalette.accent}`}
                />
              )}
            </div>
          </div>
        </div>

        {/* Footer with Template Name and Indicator - Compact */}
        <div className="flex-shrink-0 px-2.5 py-1.5 bg-card border-t">
          <div className="flex items-center gap-2">
            {/* Indicator on left - different based on mode */}
            {selectionMode === 'theme' ? (
              // Theme mode: show checkmark if selected, or empty circle
              isSelected ? (
                <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <Check className="h-3 w-3 text-white" />
                </div>
              ) : (
                <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 flex-shrink-0" />
              )
            ) : (
              // Layout mode: show expandable chevron
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-primary transition-transform duration-200 flex-shrink-0',
                  isExpanded && 'rotate-180'
                )}
              />
            )}

            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground truncate">
                {template.name}
              </p>
            </div>

            {/* Expand button for theme mode - separate from selection */}
            {selectionMode === 'theme' && (
              <button
                type="button"
                onClick={handleExpandClick}
                className={cn(
                  "flex-shrink-0 p-1 rounded hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-colors",
                  isExpanded && "bg-accent"
                )}
                title={isExpanded ? "Hide layouts" : "View layouts"}
              >
                <ChevronDown
                  className={cn(
                    'h-4 w-4 text-primary transition-transform duration-200',
                    isExpanded && 'rotate-180'
                  )}
                />
              </button>
            )}

            {/* Layout count badge on right */}
            <div className={cn(
              "flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-medium",
              selectionMode === 'theme' && isSelected
                ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
                : "bg-accent text-accent-foreground"
            )}>
              {template.layouts.length} {selectionMode === 'theme' ? 'layouts' : ''}
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
